import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Plus, Trash2, Megaphone, Type, Image as ImageIcon } from "lucide-react";
import { http, formatApiError } from "../lib/api";

const inp = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm";
const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });

export const activeBanner = (banners = []) => {
  const t = today();
  const on = banners.filter((b) => b.enabled !== false);
  const dated = on.filter((b) => (b.start || b.end) && (b.start || "") <= t && t <= (b.end || "9999"));
  if (dated.length) return dated.reduce((a, b) => ((b.start || "") > (a.start || "") ? b : a));
  return on.find((b) => !b.start && !b.end) || null;
};

export const useBannerSrc = (b) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!b) { setSrc(""); return; }
    if (b.kind === "image") { setSrc(b.image_id ? `${process.env.REACT_APP_BACKEND_URL}/api/public/xaltix/sig-banner/${b.image_id}` : ""); return; }
    const t = setTimeout(() => http.post("/xaltix/signature/banner-preview", b, { responseType: "blob" }).then((r) => setSrc(URL.createObjectURL(r.data))).catch(() => {}), 400);
    return () => clearTimeout(t);
  }, [b?.id, b?.kind, b?.text, b?.button_label, b?.image_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return src;
};

const BannerThumb = ({ b }) => {
  const src = useBannerSrc(b);
  return src ? <img src={src} alt="" className="w-full max-w-[450px] rounded border border-slate-200" data-testid={`sig-banner-thumb-${b.id}`} /> : <div className="text-xs text-slate-400 italic">Aucun aperçu</div>;
};

const BannerCard = ({ b, set, remove, isActive }) => {
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const r = await http.post("/xaltix/signature/banner-image", fd); set({ image_id: r.data.id }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const field = (k, l, props = {}) => (
    <label className="block"><span className="text-[11px] text-slate-500">{l}</span>
      <input className={inp} value={b[k] || ""} onChange={(e) => set({ [k]: e.target.value || (props.type === "date" ? null : "") })} data-testid={`sig-banner-${k}-${b.id}`} {...props} /></label>
  );
  return (
    <div className={`border rounded-lg p-4 space-y-3 transition-colors ${isActive ? "border-[#C9962E] bg-amber-50/40" : "border-slate-200"}`} data-testid={`sig-banner-${b.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${inp} flex-1 min-w-[160px] font-semibold`} placeholder="Nom (ex. Nouvel an)" value={b.label || ""} onChange={(e) => set({ label: e.target.value })} data-testid={`sig-banner-label-${b.id}`} />
        {isActive && <span className="text-[11px] font-bold uppercase tracking-wider text-[#8a6416] bg-amber-100 rounded-full px-2.5 py-1" data-testid={`sig-banner-active-${b.id}`}>Affiché aujourd'hui</span>}
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs font-semibold">
          {[["text", "Texte", Type], ["image", "Image", ImageIcon]].map(([k, l, I]) => (
            <button key={k} type="button" onClick={() => set({ kind: k })} className={`px-3 py-1.5 inline-flex items-center gap-1 transition-colors ${b.kind === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`sig-banner-kind-${k}-${b.id}`}><I className="h-3.5 w-3.5" />{l}</button>
          ))}
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={b.enabled !== false} onChange={(e) => set({ enabled: e.target.checked })} data-testid={`sig-banner-enabled-${b.id}`} />Actif</label>
        <button type="button" onClick={remove} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" aria-label="Supprimer" data-testid={`sig-banner-delete-${b.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        {b.kind === "text" ? <><div className="sm:col-span-2">{field("text", "Texte du bandeau")}</div>{field("button_label", "Texte du bouton")}</> : (
          <label className="sm:col-span-3 flex items-center gap-2 border border-dashed border-slate-300 rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`sig-banner-upload-${b.id}`}>
            <ImagePlus className="h-4 w-4 text-slate-500" />{b.image_id ? "Remplacer l'image" : "Choisir une image (PNG, JPG, GIF — idéalement 1200 × 200 px)"}
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} /></label>
        )}
        {field("link", "Lien au clic", { placeholder: "https://…" })}
        {field("start", "Afficher du", { type: "date" })}
        {field("end", "au", { type: "date" })}
      </div>
      {!b.start && !b.end && <p className="text-[11px] text-slate-500">Sans dates : bandeau par défaut, affiché quand aucun bandeau daté n'est en cours.</p>}
      <BannerThumb b={b} />
    </div>
  );
};

export const SigBanners = ({ banners, onChange }) => {
  const act = activeBanner(banners);
  const upd = (id, p) => onChange(banners.map((b) => (b.id === id ? { ...b, ...p } : b)));
  const add = () => onChange([...banners, { id: Math.random().toString(36).slice(2, 10), label: "", kind: "text", text: "Demandez votre démo gratuite", button_label: "Je réserve", link: "", image_id: "", start: null, end: null, enabled: true }]);
  return (
    <div className="mt-5 space-y-3" data-testid="sig-banners">
      <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#C9962E]" /><h3 className="font-bold text-sm">Bandeaux sous la signature</h3>
        <button type="button" onClick={add} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50" data-testid="sig-banner-add"><Plus className="h-3.5 w-3.5" />Ajouter un bandeau</button></div>
      <p className="text-xs text-slate-500">Le bandeau affiché change tout seul selon les dates, même dans les signatures déjà copiées. Un bandeau daté passe avant le bandeau par défaut.</p>
      {banners.map((b) => <BannerCard key={b.id} b={b} isActive={act?.id === b.id} set={(p) => upd(b.id, p)} remove={() => onChange(banners.filter((x) => x.id !== b.id))} />)}
    </div>
  );
};
